import React, { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import { DOC_FOOTER, withinPhrase } from "../helper/rxData";
import "../styles/prescriptionDoc.scss";

/**
 * Prescription document — the right pane of "Screen / review prescription".
 * Source: Figma Prescription document (1762:16579), geometry read off the Dev
 * Mode subtree: a 728 page with 48px gutters over a 632 content column.
 *
 * This is a LIVE preview. It renders purely from `doc` — the output of
 * buildDocument() — and keeps no copy of the clinical data, so the page cannot
 * drift from what is ticked on the left.
 *
 * PAGINATED, like a PDF viewer: the content is split into A4 sheets stacked with
 * gutters between them, rather than one tall sheet with a break drawn across it.
 * The split is real — a medicine row or an instruction that does not fit moves to
 * the next sheet instead of being cut in half, which is what wkhtmltopdf does with
 * the `page-break-inside: avoid` rules in the PDF template.
 *
 * How: the document is laid out ONCE in a hidden measuring copy, each break unit's
 * height is read from it, units are packed into pages, and the visible sheets are
 * rendered from that packing. Measuring a hidden copy rather than the sheets
 * themselves keeps the input independent of the output — measuring the sheets
 * would feed each layout back into the next and never settle.
 */

/* The lavender band's three lines. `key` indexes into `doc`; a line renders
   only while its array has something in it.

   Chief Complaint leads (product, 2026-09-10), ahead of the Figma board, which
   draws Diagnosis first (3031:9884-9886). The generated PDF's band is ordered
   to match in api-prescription-service, so the preview and the document agree. */
const SUMMARY_LINES = [
  { key: "complaints", label: "Chief Complaint:" },
  { key: "diagnosis", label: "Diagnosis:" },
  { key: "reports", label: "Reports:" },
];

/* Metric cells under the patient row (Metrics 1762:16592) — four equal columns. */
const metricsOf = (patient) => [
  { label: "Age/Gender", value: patient.ageGender },
  { label: patient.heightLabel || "Height", value: patient.height },
  { label: "Weight (Kg)", value: patient.weight },
  { label: "BMI", value: patient.bmi },
];

/** A4 portrait, the size the PDF is generated at (595x842pt). */
const A4_RATIO = 297 / 210;

/** The section element an atom's `wrap` tag asks for. */
const wrapClassOf = (w) => {
  if (!w) return null;
  if (w.indexOf("sec:") === 0) return "rxdoc-section";
  if (w === "patient") return "rxdoc-patient";
  if (w === "summary") return "rxdoc-summary";
  return null;
};

const PrescriptionDocument = ({ doc }) => {
  const { prescriber, patient, medicines, otherInstructions, investigations, referrals } = doc;
  const summary = SUMMARY_LINES.filter((line) => (doc[line.key] || []).length > 0);

  /* ---------- break units ----------
     Each atom is the smallest thing that must not be split across a page — one
     medicine row, one instruction, one referral. `wrap` names the section element
     to rebuild around it (a section continuing onto page 2 gets its wrapper back,
     without repeating the title); `list` groups atoms back into their <ol> so the
     numbering carries on; `keepWithNext` stops a heading being stranded alone at
     the foot of a page. */
  const atoms = [];
  const add = (key, wrap, node, extra) =>
    atoms.push(Object.assign({ key, wrap: wrap || null, node, list: null }, extra));

  add(
    "head",
    null,
    <header className="rxdoc-head" key="head">
      <div className="rxdoc-head-row">
        <div className="rxdoc-presc">
          <div className="rxdoc-presc-name">{prescriber.name}</div>
          <div className="rxdoc-presc-qual">{prescriber.qualification}</div>
          <div className="rxdoc-presc-lic">{prescriber.license}</div>
        </div>
        {/* No logo asset in the repo — the wordmark is set as text (1762:16586). */}
        <div className="rxdoc-wordmark">zyla</div>
      </div>
      <div className="rxdoc-head-rule" />
    </header>
  );

  add(
    "patient",
    null,
    <section className="rxdoc-patient" key="patient">
      <div className="rxdoc-patient-row">
        <span>
          <span className="rxdoc-label">Patient Name: </span>
          <span className="rxdoc-value">{patient.name}</span>
        </span>
        <span>
          <span className="rxdoc-label">Consultation Date: </span>
          <span className="rxdoc-value">{doc.consultationDate}</span>
        </span>
      </div>
      <div className="rxdoc-metrics">
        {metricsOf(patient).map((m) => (
          <div className="rxdoc-metric" key={m.label}>
            <div className="rxdoc-metric-label">{m.label}</div>
            <div className="rxdoc-metric-value">{m.value || "-"}</div>
          </div>
        ))}
      </div>
    </section>
  );

  if (summary.length > 0) {
    add(
      "summary",
      null,
      <section className="rxdoc-summary" key="summary">
        {summary.map((line) => (
          <div className="rxdoc-summary-line" key={line.key}>
            <span className="rxdoc-summary-label">{line.label} </span>
            {doc[line.key].join(", ")}
          </div>
        ))}
      </section>
    );
  }

  if (medicines.length > 0) {
    add(
      "med-title",
      "sec:med",
      <h3 className="rxdoc-section-title" key="med-title">
        Medicine Prescription
      </h3>,
      { keepWithNext: true }
    );
    /* Board 6360:4379 — FOUR columns on a filled header strip. The dose is no
       longer a column of its own: it rides inside the frequency as
       "Morning (1), Afternoon (2), Night (1)", which is the only form that can
       state a per-slot regimen without loss (rxMappers docFrequency). */
    add(
      "med-head",
      "sec:med",
      <div className="rxdoc-med-grid rxdoc-med-head" role="row" key="med-head">
        <span role="columnheader">Medicine</span>
        <span role="columnheader">Frequency (dose qty.)</span>
        <span role="columnheader">Instructions</span>
        {/* No collapse control. Board 6360:4379 has none, and this pane is a
            preview of a printed document — a section the reader can fold away
            is a thing the printed prescription cannot do, so the affordance
            only ever misrepresented it. */}
        <span role="columnheader">Time period</span>
      </div>,
      { keepWithNext: true }
    );
    medicines.forEach((m, i) => {
      const row = (
        <div className="rxdoc-med-grid rxdoc-med-row" role="row" key={`med-${i}`}>
          <span className="rxdoc-med-name" role="cell">
            {i + 1}. {m.name}
            {/* The dosage form is a grey suffix on the board, not a prefix. */}
            {m.form ? <span className="rxdoc-med-form"> ({m.form})</span> : null}
          </span>
          <span className="rxdoc-med-cell" role="cell">{m.frequency}</span>
          {/* None given reads "-", as an empty height or BMI does above. */}
          <span className="rxdoc-med-cell" role="cell">{m.instructions || "-"}</span>
          <span className="rxdoc-med-cell" role="cell">
            {m.duration}
            {/* The span the duration covers, under it in grey (6360:4397).
                Absent for a course with no start date or an indefinite one,
                where there is no span to state. */}
            {m.dates ? <span className="rxdoc-med-dates">{m.dates}</span> : null}
          </span>
        </div>
      );
      add(
        `med-${i}`,
        "sec:med",
        /* A medicine with a Description is ONE break unit — its row and the line
           under it (8777:7861) never land on different pages — so the two share
           a wrapper. Without one the row alone is the unit, exactly as before. */
        m.description ? (
          <div className="rxdoc-med-item" key={`med-${i}`}>
            {row}
            <div className="rxdoc-med-desc">{m.description}</div>
          </div>
        ) : (
          row
        )
      );
    });
  }

  if (investigations.length > 0) {
    add(
      "inv-title",
      "sec:inv",
      /* Placed BEFORE Other Instructions, per board 3031:9853 — which is also
         the order the builder pane on the left uses (ReviewPrescription.js:231
         "Advised investigations", :232 "Custom instruction"). The preview was
         the only one of the three that disagreed.

         THE GENERATED PDF STILL PRINTS THEM THE OTHER WAY ROUND, and calls this
         section "Investigation" rather than "Advised investigations"
         (api-prescription-service utils/new.html:607 Other Instructions, :623
         Investigation). Both are backend changes to that template; until they
         land, this pane and the document it previews differ in the order of
         these two sections and in this heading's wording. */
      <h3 className="rxdoc-section-title" key="inv-title">
        Advised investigations
      </h3>,
      { keepWithNext: true }
    );
    /* Board 3031:9922 numbers them one per line; a comma run hid where one
       test's instruction ended and the next began. */
    investigations.forEach((text, i) => {
      add(`inv-${i}`, "sec:inv", <li key={`inv-${i}`}>{text}</li>, {
        list: "inv",
        listIndex: i,
      });
    });
  }

  if (otherInstructions.length > 0) {
    add(
      "instr-title",
      "sec:instr",
      <h3 className="rxdoc-section-title" key="instr-title">
        Other Instructions
      </h3>,
      { keepWithNext: true }
    );
    otherInstructions.forEach((text, i) => {
      add(
        `instr-${i}`,
        "sec:instr",
        <div className="rxdoc-instr-item" key={`instr-${i}`}>
          {i + 1}. {text}
        </div>,
        { list: "instr", listIndex: i }
      );
    });
  }

  if (referrals.length > 0) {
    add(
      "ref-title",
      "sec:ref",
      <h3 className="rxdoc-section-title" key="ref-title">
        Referral
      </h3>,
      { keepWithNext: true }
    );
    referrals.forEach((r, i) => {
      add(
        `ref-${i}`,
        "sec:ref",
        <div className="rxdoc-ref-item" key={r.id || `ref-${i}`}>
          <div>
            {/* A window and a deadline need different sentences — "within 1 week of
                this prescription" vs "by 5 Sep, 2026". withinPhrase is shared with
                the card and mirrored in the PDF transformer so all three agree. */}
            {i + 1}. {r.specialty}
            {r.within ? ` — ${withinPhrase(r.within)}` : ""}.
          </div>
          {/* Omitted when blank, matching the PDF transformer, which skips an empty
              reason. Rendered unconditionally, the preview printed a bare "Reason: ."
              under every referral written without one. */}
          {r.docReason || r.reason ? (
            <div>Reason: {String(r.docReason || r.reason).replace(/\.$/, "")}.</div>
          ) : null}
          {r.reportsShared && <div>Reports shared: {r.reportsShared}</div>}
        </div>
      );
    });
  }

  /* The signature block (board 3031:9926): the doctor's name in a script face,
     then the name again plainly, then the speciality — right-aligned.

     THE SCRIPT LINE IS A TYPOGRAPHIC SIGNATURE, NOT A SCANNED ONE, and the
     generated PDF does not carry it: that template draws
     `<img src="{{.DoctorSignature}}">` (utils/new.html) and prints nothing when
     the field is empty, which it is for every doctor outside the ported
     signature table. So for most prescriptions this pane shows a name where the
     PDF shows blank. That difference is the board's, adopted deliberately — the
     alternative was a preview whose signature area was empty on every
     prescription.

     A REAL SIGNATURE IMAGE WINS OVER IT, and `prescriber.signature` IS
     populated now: ReviewPrescription looks the prescriber up in
     helper/doctorSignatures.js, the same table the save body uses, so the two
     agree about whether a prescription is signed. Drawing the image AND the
     script name would put two signatures on one document, so it is one or the
     other.

     aria-hidden: it repeats, verbatim, the name in the very next line. Left
     readable, every screen reader would announce the prescriber twice. */
  add(
    "sign",
    null,
    <section className="rxdoc-signature" key="sign">
      {prescriber.signature ? (
        <img className="rxdoc-sign-img" src={prescriber.signature} alt="" />
      ) : prescriber.name ? (
        /* Guarded on the name: before the real prescriber resolves it is "" and
           an empty script line would still take its 28px, nudging the block. */
        <div className="rxdoc-sign-mark" aria-hidden="true">
          {prescriber.name}
        </div>
      ) : null}
      <div className="rxdoc-sign-name">{prescriber.name}</div>
      <div className="rxdoc-sign-qual">{prescriber.qualification}</div>
    </section>
  );

  /* ---------- measure, then pack ---------- */
  const measureRef = useRef(null);
  const [pages, setPages] = useState(null);
  // Re-measure whenever the set of atoms changes. Keys encode the content, so a
  // changed medicine or a toggled table produces a different signature.
  const signature = atoms.map((a) => a.key).join("|");

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return undefined;
    const measure = () => {
      const width = el.getBoundingClientRect().width;
      if (!(width > 0)) return;
      const pageH = width * A4_RATIO;
      const footEl = el.querySelector(".rxdoc-footer");
      // The footer is printed at the foot of EVERY page (wkhtmltopdf's
      // page.FooterCenter), so its height is unavailable to content on all of them.
      const usable = pageH - (footEl ? footEl.getBoundingClientRect().height : 0);
      if (!(usable > 0)) return;

      const nodes = Array.from(el.querySelectorAll("[data-atom]"));
      if (!nodes.length) return;
      // Heights come from the gap to the NEXT atom, so collapsing margins between
      // units are counted exactly once — reading each box's own height would drop
      // them and pack the pages too full.
      const tops = nodes.map((n) => n.offsetTop);
      const heights = nodes.map((n, i) =>
        i + 1 < nodes.length ? tops[i + 1] - tops[i] : n.offsetTop + n.offsetHeight - tops[i]
      );

      const out = [[]];
      let origin = tops[0];
      nodes.forEach((n, i) => {
        const current = out[out.length - 1];
        const used = tops[i] - origin;
        if (current.length && used + heights[i] > usable) {
          out.push([]);
          origin = tops[i];
        }
        out[out.length - 1].push(n.getAttribute("data-atom"));
      });

      // A heading must not be left alone at the foot of a page. Walk backwards so
      // moving a title also carries any title above it.
      for (let i = out.length - 2; i >= 0; i -= 1) {
        const page = out[i];
        while (page.length > 1) {
          const lastKey = page[page.length - 1];
          const atom = atoms.find((a) => a.key === lastKey);
          if (!atom || !atom.keepWithNext) break;
          out[i + 1].unshift(page.pop());
        }
      }
      setPages(out.filter((p) => p.length));
    };
    measure();
    if (typeof window.ResizeObserver !== "function") return undefined;
    const ro = new window.ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  /* ---------- render ---------- */
  const byKey = {};
  atoms.forEach((a) => {
    byKey[a.key] = a;
  });

  // Regroup a page's atoms back into their <ol> runs so list numbering continues.
  const renderRun = (run, measuring) => {
    // In the measuring copy every atom carries its key, so the packer can read the
    // laid-out offsets back off the DOM.
    const nodeOf = (a) =>
      measuring ? React.cloneElement(a.node, { "data-atom": a.key }) : a.node;
    const out = [];
    let i = 0;
    while (i < run.length) {
      const listId = run[i].list;
      if (listId) {
        const group = [];
        while (i < run.length && run[i].list === listId) {
          group.push(run[i]);
          i += 1;
        }
        const Tag = listId === "inv" ? "ol" : "div";
        const cls = listId === "inv" ? "rxdoc-inv-list" : "rxdoc-instr-list";
        // `start` continues the numbering onto the next sheet.
        const props = { className: cls, key: `list-${group[0].key}` };
        if (Tag === "ol") props.start = group[0].listIndex + 1;
        out.push(React.createElement(Tag, props, group.map(nodeOf)));
      } else {
        out.push(nodeOf(run[i]));
        i += 1;
      }
    }
    return out;
  };

  // Rebuild the section wrappers a page's atoms belong to.
  const renderPage = (keys, measuring) => {
    const items = keys.map((k) => byKey[k]).filter(Boolean);
    const out = [];
    let i = 0;
    while (i < items.length) {
      const w = items[i].wrap;
      const run = [];
      while (i < items.length && items[i].wrap === w) {
        run.push(items[i]);
        i += 1;
      }
      const cls = wrapClassOf(w);
      out.push(
        cls ? (
          <section className={cls} key={`w-${run[0].key}`}>
            {renderRun(run, measuring)}
          </section>
        ) : (
          <React.Fragment key={`w-${run[0].key}`}>{renderRun(run, measuring)}</React.Fragment>
        )
      );
    }
    return out;
  };

  const sheets = pages || [atoms.map((a) => a.key)];

  return (
    <div className="rxdoc">
      {/* The hidden copy everything is measured from. It is laid out at the real
          page width but never shown; `visibility: hidden` (not `display: none`)
          because a display-none subtree has no layout to measure. */}
      <div className="rxdoc-measure" ref={measureRef} aria-hidden="true">
        {renderPage(atoms.map((a) => a.key), true)}
        <footer className="rxdoc-footer">{DOC_FOOTER}</footer>
      </div>

      {sheets.map((keys, i) => (
        <article className="rxdoc-page" key={`page-${i}`}>
          {renderPage(keys)}
          <footer className="rxdoc-footer">{DOC_FOOTER}</footer>
        </article>
      ))}
    </div>
  );
};

/* Metric values arrive as strings from the patient card's inputs, but stay
   tolerant of a number in case a caller hands one over unformatted. */
const metricValue = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

PrescriptionDocument.propTypes = {
  doc: PropTypes.shape({
    prescriber: PropTypes.shape({
      name: PropTypes.string,
      qualification: PropTypes.string,
      license: PropTypes.string,
      // The signature IMAGE url, as the PDF uses. Absent for every phr
      // prescription today — phr sends doctorSignature: null.
      signature: PropTypes.string,
    }).isRequired,
    consultationDate: PropTypes.string,
    patient: PropTypes.shape({
      name: PropTypes.string,
      ageGender: PropTypes.string,
      height: metricValue,
      heightLabel: PropTypes.string,
      weight: metricValue,
      bmi: metricValue,
    }).isRequired,
    diagnosis: PropTypes.arrayOf(PropTypes.string).isRequired,
    complaints: PropTypes.arrayOf(PropTypes.string).isRequired,
    reports: PropTypes.arrayOf(PropTypes.string).isRequired,
    medicines: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        form: PropTypes.string,
        frequency: PropTypes.string,
        instructions: PropTypes.string,
        duration: PropTypes.string,
        dates: PropTypes.string,
        description: PropTypes.string,
      })
    ).isRequired,
    otherInstructions: PropTypes.arrayOf(PropTypes.string).isRequired,
    investigations: PropTypes.arrayOf(PropTypes.string).isRequired,
    referrals: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        specialty: PropTypes.string,
        within: PropTypes.string,
        reason: PropTypes.string,
        docReason: PropTypes.string,
        reportsShared: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
};

export default PrescriptionDocument;
